/* four-pillars.js — <four-pillars> (sections/four-pillars.liquid).

   The section is complete without this file: the first pillar is open and the
   picture beside it shows. The script adds the behaviour, in one of three
   modes that are re-decided whenever the breakpoint or the reduced-motion
   preference changes:

   * pinned    — 750px and up, "Pin while scrolling" on, motion allowed. Sets
                 [data-pinned], which switches on the CSS sticky row and the
                 spacer after it; the scroll position inside the spacer picks
                 the active pillar. Moving the mouse onto a pillar, or pressing
                 its title, scrolls to that pillar's slot (as in the source),
                 so the page and the list never disagree.
   * tabs      — 750px and up otherwise: hovering a pillar or pressing its
                 title opens it.
   * accordion — below 750px: a title press opens its pillar, with the
                 picture inside the item.

   Only the pinned mode listens to scroll, and it only reads geometry: the pin
   itself is CSS. When the pinned list is taller than the screen under the
   header, CSS clips it and center() slides it (--pillars-shift) so the open
   pillar stays in view. The one box this element restyles is the next
   section's, and only with "Stick together with the next section" on (see
   syncNext()). */

const desktop = window.matchMedia('(min-width: 750px)');
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const SECTION_EVENTS = ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'];

class FourPillars extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-pillar]'));
    this.images = Array.from(this.querySelectorAll('[data-pillar-image]'));
    this.sticky = this.querySelector('.fs-pillars__sticky');
    this.track = this.querySelector('.fs-pillars__track');
    this.content = this.querySelector('.fs-pillars__content');
    this.list = this.querySelector('.fs-pillars__list');
    if (!this.items.length || !this.sticky || !this.track) return;

    this.index = Math.max(0, this.items.findIndex((item) => item.hasAttribute('data-active')));

    this.onClick = (event) => {
      const trigger = event.target.closest('[data-pillar-trigger]');
      if (!trigger || !this.contains(trigger)) return;
      const index = this.items.indexOf(trigger.closest('[data-pillar]'));
      if (index < 0) return;
      this.activate(index);
      if (this.hasAttribute('data-pinned')) this.scrollToPillar(index, 'smooth');
    };
    this.addEventListener('click', this.onClick);

    /* A mouse moving onto a pillar opens it, as in the source; while pinned
       the page also glides to that pillar's slot. Only real movement counts:
       the synthetic move a browser sends after a scroll (same position) would
       otherwise let a list shifting under a still cursor open pillar after
       pillar. */
    this.onPointerMove = (event) => {
      if (event.pointerType !== 'mouse') return;
      const moved = event.clientX !== this.pointerX || event.clientY !== this.pointerY;
      this.pointerX = event.clientX;
      this.pointerY = event.clientY;
      if (!moved || !desktop.matches) return;
      const index = this.items.indexOf(event.target.closest('[data-pillar]'));
      if (index < 0 || index === this.index) return;
      this.activate(index);
      if (this.hasAttribute('data-pinned')) this.scrollToPillar(index, 'smooth');
    };
    this.list.addEventListener('pointermove', this.onPointerMove);

    this.onScroll = () => {
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(() => {
        this.frame = 0;
        this.syncScroll();
      });
    };
    this.onResize = () => {
      this.syncNext();
      this.onScroll();
    };
    this.onModeChange = () => this.syncMode();
    desktop.addEventListener('change', this.onModeChange);
    reducedMotion.addEventListener('change', this.onModeChange);

    /* Theme editor: a section added, removed or moved can change which
       section comes next. */
    this.onSectionChange = () => {
      clearTimeout(this.sectionTimer);
      this.sectionTimer = setTimeout(() => this.syncNext(), 60);
    };
    SECTION_EVENTS.forEach((type) => document.addEventListener(type, this.onSectionChange));

    /* Theme editor: selecting a pillar opens it, and in pinned mode also
       brings the page to its slot — otherwise the next scroll event would
       hand the list straight back to whichever pillar the page is on. */
    this.onBlockSelect = (event) => {
      if (!this.contains(event.target)) return;
      const index = this.items.indexOf(event.target.closest('[data-pillar]'));
      if (index < 0) return;
      this.activate(index);
      if (this.hasAttribute('data-pinned')) this.scrollToPillar(index, 'instant');
    };
    document.addEventListener('shopify:block:select', this.onBlockSelect);

    /* Tabbing down a clipped list brings the focused title into the window
       without opening it. */
    this.onFocus = (event) => {
      const trigger = event.target.closest('[data-pillar-trigger]');
      if (trigger) this.center(this.items.indexOf(trigger.closest('[data-pillar]')));
    };
    this.addEventListener('focusin', this.onFocus);

    /* The sticky offset centres the row in the space under the header, and
       CSS cannot read the row's own height — publish it. */
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        this.measure();
        this.center();
        this.syncNext();
      });
      this.resizeObserver.observe(this.sticky);
    }

    this.syncMode();
  }

  disconnectedCallback() {
    if (!this.onClick) return;
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('focusin', this.onFocus);
    this.list.removeEventListener('pointermove', this.onPointerMove);
    desktop.removeEventListener('change', this.onModeChange);
    reducedMotion.removeEventListener('change', this.onModeChange);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    SECTION_EVENTS.forEach((type) => document.removeEventListener(type, this.onSectionChange));
    clearTimeout(this.sectionTimer);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    this.listen(false);
    this.releaseNext();
  }

  syncMode() {
    const pinned = this.hasAttribute('data-pin') && desktop.matches && !reducedMotion.matches;
    this.toggleAttribute('data-pinned', pinned);
    this.listen(pinned);
    this.measure();
    this.center();
    this.syncNext();
    this.syncScroll();
  }

  listen(on) {
    if (on === Boolean(this.listening)) return;
    this.listening = on;
    if (on) {
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      return;
    }
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    if (this.frame) window.cancelAnimationFrame(this.frame);
    this.frame = 0;
  }

  /* "Stick together with the next section" ([data-pin-next]), the source's
     combine pin: while pinned, the section after this one rides directly
     under the pillar row, and both scroll on together once the pin ends. Its
     wrapper is pulled up over the spacer and made taller by the same distance
     (height, not padding: a sticky box only travels inside its containing
     block's content box), so its first block can stick just under the row
     for the whole pin; the wrapper lets clicks through to the row it
     overlaps. As in the source, a short next section keeps the rest of the
     page below the screen until the pin ends. A section that pins itself
     (MaxLock core) is left alone. */
  syncNext() {
    const next = this.findNext();
    if (next !== this.next) {
      this.releaseNext();
      if (!next) return;
      this.next = next;
      this.nextInner = Array.from(next.children).find((el) => !/^(LINK|SCRIPT|STYLE|TEMPLATE)$/.test(el.tagName)) || null;
      if (this.resizeObserver && this.nextInner) this.resizeObserver.observe(this.nextInner);
    }
    if (!this.next || !this.nextInner) return;
    const distance = this.track.offsetHeight;
    const rowBottom = this.stickyTop() + this.sticky.offsetHeight;
    const height = this.nextInner.offsetHeight;
    const below = Math.max(0, window.innerHeight - rowBottom - height);
    Object.assign(this.next.style, {
      position: 'relative',
      marginBlockStart: `${-distance}px`,
      minBlockSize: `${height + distance}px`,
      marginBlockEnd: `${below}px`,
      pointerEvents: 'none',
    });
    Object.assign(this.nextInner.style, { position: 'sticky', top: `${rowBottom}px`, pointerEvents: 'auto' });
  }

  findNext() {
    if (!this.isConnected || !this.hasAttribute('data-pin-next') || !this.hasAttribute('data-pinned')) return null;
    let next = (this.closest('.shopify-section') || this).nextElementSibling;
    while (next && !next.classList.contains('shopify-section')) next = next.nextElementSibling;
    if (!next || next.querySelector('maxlock-core[data-animate], four-pillars[data-pin]')) return null;
    return next;
  }

  releaseNext() {
    if (!this.next) return;
    ['position', 'marginBlockStart', 'minBlockSize', 'marginBlockEnd', 'pointerEvents'].forEach((property) => {
      this.next.style[property] = '';
    });
    if (this.nextInner) {
      ['position', 'top', 'pointerEvents'].forEach((property) => {
        this.nextInner.style[property] = '';
      });
      if (this.resizeObserver) this.resizeObserver.unobserve(this.nextInner);
    }
    this.next = null;
    this.nextInner = null;
  }

  measure() {
    this.style.setProperty('--pillars-h', `${this.sticky.offsetHeight}px`);
  }

  /* Slide a pinned list that is taller than its window so pillar `index`
     sits in the middle — the source's centerList(), clamped so the list never
     leaves a gap above or below. Heights are the ones the panels are heading
     to (only the open pillar shows its panel), not the mid-transition ones. */
  center(index = this.index) {
    if (!this.hasAttribute('data-pinned') || index < 0) {
      this.style.removeProperty('--pillars-shift');
      return;
    }
    /* The window is the clip height (the space under the header), not the
       box's current height, which trails an opening panel. */
    const box = getComputedStyle(this.content);
    const view = (parseFloat(box.maxHeight) || this.content.clientHeight) - parseFloat(box.paddingTop) - parseFloat(box.paddingBottom);
    const gap = parseFloat(getComputedStyle(this.list).rowGap) || 0;
    const heights = this.items.map((item, i) => {
      const title = item.querySelector('.fs-pillars__title');
      const inner = item.querySelector('.fs-pillars__panel-inner');
      return title.offsetHeight + (i === this.index && inner ? inner.scrollHeight : 0);
    });
    const total = heights.reduce((sum, height) => sum + height + gap, -gap);
    const before = heights.slice(0, index).reduce((sum, height) => sum + height + gap, 0);
    const shift = Math.min(Math.max(0, total - view), Math.max(0, before + heights[index] / 2 - view / 2));
    this.style.setProperty('--pillars-shift', `${-shift}px`);
  }

  /* The used sticky offset, in px — computed style resolves the calc(). */
  stickyTop() {
    return parseFloat(getComputedStyle(this.sticky).top) || 0;
  }

  syncScroll() {
    if (!this.hasAttribute('data-pinned')) return;
    const distance = this.track.offsetHeight;
    if (!distance) return;
    const scrolled = this.stickyTop() - this.getBoundingClientRect().top;
    const slot = Math.floor((scrolled / distance) * this.items.length);
    this.activate(Math.min(this.items.length - 1, Math.max(0, slot)));
  }

  /* Park the page in the middle of the pillar's slot, so a small scroll
     either way does not immediately flip to its neighbour. */
  scrollToPillar(index, behavior) {
    const distance = this.track.offsetHeight;
    const top =
      window.scrollY +
      this.getBoundingClientRect().top -
      this.stickyTop() +
      ((index + 0.5) / this.items.length) * distance;
    window.scrollTo({ top: Math.max(0, top), behavior });
  }

  activate(index) {
    if (index === this.index && this.items[index].hasAttribute('data-active')) return;
    this.index = index;
    this.items.forEach((item, i) => {
      const active = i === index;
      item.toggleAttribute('data-active', active);
      const trigger = item.querySelector('[data-pillar-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', String(active));
    });
    this.images.forEach((image, i) => image.toggleAttribute('data-active', i === index));
    this.center();
  }
}

if (!customElements.get('four-pillars')) customElements.define('four-pillars', FourPillars);
